import { NS } from '@ownyou/shared-types';
import {
  upsertLoveEntity,
  type LoveEntity,
  type MissionOpportunity,
} from '@ownyou/ikigai';

type NamespaceTuple = readonly [string, ...string[]];

export interface IkigaiDetectionPersistenceStore {
  put<T>(namespace: NamespaceTuple, key: string, value: T): Promise<void>;
}

export interface IkigaiDetectionPersistenceCounts {
  entityCount: number;
  opportunityCount: number;
  opportunityStoreWriteCount: number;
}

export function ikigaiOpportunityStoreKey(opportunity: MissionOpportunity): string {
  return `opp-${opportunity.entityName.toLowerCase().replace(/\s+/g, '-')}-${opportunity.agentType}-${opportunity.deadline ?? 'evergreen'}`;
}

export async function persistIkigaiEntities(
  userId: string,
  entities: LoveEntity[],
  store: IkigaiDetectionPersistenceStore,
): Promise<number> {
  for (const entity of entities) {
    await upsertLoveEntity(userId, entity, store as Parameters<typeof upsertLoveEntity>[2]);
  }

  return entities.length;
}

export async function persistIkigaiOpportunities(
  userId: string,
  opportunities: MissionOpportunity[],
  store: IkigaiDetectionPersistenceStore,
): Promise<number> {
  const namespace = NS.ikigaiOpportunities(userId);
  for (const opportunity of opportunities) {
    await store.put(namespace, ikigaiOpportunityStoreKey(opportunity), opportunity);
  }

  return opportunities.length;
}

export async function persistIkigaiDetectionArtifacts(
  userId: string,
  entities: LoveEntity[],
  opportunities: MissionOpportunity[],
  store: IkigaiDetectionPersistenceStore,
): Promise<IkigaiDetectionPersistenceCounts> {
  const entityCount = await persistIkigaiEntities(userId, entities, store);
  const opportunityStoreWriteCount = await persistIkigaiOpportunities(userId, opportunities, store);

  return {
    entityCount,
    opportunityCount: opportunities.length,
    opportunityStoreWriteCount,
  };
}
